import type { ReservationInWindow } from '@/api/processStepsApi'
import type { ScheduleEvent } from '@/types/teamSchedule'
import { PROCESS_EVENT_TYPE_CODE } from '@/types/maintenanceTask'
import { localDayIso, reservationHoursOnDay } from '@/lib/timeWindow'
import { RESERVATION_ROW_PREFIX, bookedFor, reservationPhase } from '@/lib/reservationTasks'

// ─── Process bookings on the Team Schedule ───────────────────────────────────
//
// The Team Schedule is a technician × weekday grid. A process-step booking
// belongs here because it is work someone scheduled against a machine — but it
// is NOT a maintenance event: nothing can start it, end it, reassign it or drag
// it, because its lifecycle lives on the process step (ProcessStepExecution).
// Every card produced here is `readOnly`.
//
// A booking that runs across several days gets one card per weekday it touches.
// The grid has a cell per day, so a single card on the start day would leave the
// machine looking free on the days it is still occupied — the same defect the
// month calendar had.
//
// Pure and dependency-free so it can be unit-tested.

/**
 * The same prefix the worklist rows use, from the same constant — not a second
 * copy of the literal.
 *
 * Both surfaces are asking one question ("is this derived from a reservation,
 * and therefore not mine to mutate?"), so they get one answer. Two independent
 * `'reservation-'` literals with two identical predicates was an invitation for
 * one to be changed and the other missed, and a card id would still have passed
 * the row check regardless.
 */
export const RESERVATION_CARD_PREFIX = RESERVATION_ROW_PREFIX

/** True for cards produced by this module — used to refuse mutation paths. */
export function isReservationCard(id: string): boolean {
  return id.startsWith(RESERVATION_CARD_PREFIX)
}

/** Every local day a window touches, inclusive of both ends. */
export function daysCovered(startsAt: string, endsAt: string): string[] {
  const first = localDayIso(startsAt)
  const last = localDayIso(endsAt)
  if (!first || !last || last < first) return first ? [first] : []

  const out: string[] = []
  const cursor = new Date(`${first}T00:00:00`)
  // Bounded so a malformed far-future end can never spin: a booking spanning
  // more than a year is not a booking, it is bad data.
  for (let guard = 0; guard < 366; guard += 1) {
    const iso = localDayIso(cursor)
    out.push(iso)
    if (iso >= last) break
    cursor.setDate(cursor.getDate() + 1)
  }
  return out
}

export function reservationsToScheduleEvents(
  reservations: ReservationInWindow[],
  ctx: {
    /** Local ISO day → column index. Days outside the visible week are absent. */
    dayIndexByIso: Map<string, number>
    /** Lower-cased email → User id, for placing a booking on its booker's row. */
    technicianIdByEmail: Map<string, string>
    /** Equipment unit id → display location. */
    locationById: Map<string, string>
  },
): ScheduleEvent[] {
  const out: ScheduleEvent[] = []

  for (const res of reservations) {
    // `reservedBy` is the actor string the booking recorded. It is an email in
    // practice, so it resolves to a technician row; when it does not, the card
    // goes to `unassigned` rather than inventing a person.
    const technicianId =
      ctx.technicianIdByEmail.get(res.reservedBy.trim().toLowerCase()) ?? 'unassigned'

    const days = daysCovered(res.startsAt, res.endsAt)
    const firstDay = days[0]

    for (const iso of days) {
      const dayOffset = ctx.dayIndexByIso.get(iso)
      if (dayOffset === undefined) continue // outside Mon–Fri of this week

      const hoursToday = reservationHoursOnDay(res.startsAt, res.endsAt, iso)
      // A hold ending at exactly 00:00 nominally "touches" that day but occupies
      // none of it. Emitting a 0h card would show the machine as taken on a day
      // it is free from midnight.
      if (hoursToday <= 0) continue

      out.push({
        // Day-suffixed so a multi-day booking yields unique React keys and
        // drag ids across its cards.
        id: `${RESERVATION_CARD_PREFIX}${res.id}#${iso}`,
        technicianId,
        dayOffset,
        equipmentId: res.equipmentLabel ?? res.equipmentId,
        eventType: PROCESS_EVENT_TYPE_CODE,
        eventClass: 'process',
        // Only the first day starts at the booked time; a continuing day is
        // occupied from midnight, and saying "09:00" there would be a lie.
        timeStart: iso === firstDay ? clockHHmm(res.startsAt) : '00:00',
        // Hours occupied ON THIS DAY — the card sits in one day's cell, so the
        // total would overstate what that cell represents.
        planHours: hoursToday,
        // The real phase, in this board's vocabulary. `readOnly` below is what
        // refuses mutation, so saying a machine is in use does not offer any
        // control over it.
        status:
          reservationPhase(res) === 'finished'
            ? 'done'
            : reservationPhase(res) === 'started'
              ? 'started'
              : 'reserved',
        location: ctx.locationById.get(res.equipmentId) ?? '—',
        sop: '—',
        readOnly: true,
        window: { startsAt: res.startsAt, endsAt: res.endsAt },
        // Carried so the details dialog can answer "who is holding this machine
        // and what for" without re-fetching the reservation.
        bookedFor: bookedFor(res),
        bookedBy: res.reservedBy,
      })
    }
  }

  return out
}

/** Local HH:mm for an instant, zero-padded. */
function clockHHmm(instant: string): string {
  const d = new Date(instant)
  if (Number.isNaN(d.getTime())) return '00:00'
  return `${`${d.getHours()}`.padStart(2, '0')}:${`${d.getMinutes()}`.padStart(2, '0')}`
}
